import { Film, Sparkles, ExternalLink, Music, Lightbulb, Camera, MapPin, Users, Wrench, Palette, Megaphone } from 'lucide-react'
import { Button, Card, CardTitle, CardDescription } from '@/components/ui'
import { useGenerateMarketingDeck, useGenerateWhitepaper } from '@/hooks/useFunctions'
import { formatRelativeDate } from '@/lib/utils'
import { VaultLinkBar } from '@/components/VaultLinkBar'
import type { AppRecord } from '@/lib/types'

export function MarketingDeckPanel({ app }: { app: AppRecord }) {
  const gen = useGenerateMarketingDeck()
  const wp = useGenerateWhitepaper()
  const deck = app.marketing_deck

  return (
    <Card className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow mb-1">Marketing Asset</div>
          <CardTitle>Video concept · script · production breakdown</CardTitle>
          <CardDescription className="mt-1">
            AI drafts the script and shot list; RRM's stack handles production.
            {deck && <> · Generated <span className="text-gold-soft">{formatRelativeDate(deck.generated_at)}</span></>}
          </CardDescription>
        </div>
        <div className="flex gap-2 shrink-0">
          {app.whitepaper?.url ? (
            <a href={app.whitepaper.url} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm"><ExternalLink size={12} /> Whitepaper</Button>
            </a>
          ) : (
            <Button variant="ghost" size="sm" loading={wp.isPending} onClick={() => wp.mutate(app.slug)}>
              <Sparkles size={12} /> Whitepaper
            </Button>
          )}
          <Button variant="gold-outline" size="sm" loading={gen.isPending} onClick={() => gen.mutate(app.slug)}>
            <Film size={12} /> {deck ? 'Regenerate' : 'Generate Deck'}
          </Button>
        </div>
      </div>

      <div className="divider-editorial" />

      {deck ? (
        <div className="space-y-5">
          {deck.gamma_url && (
            <a href={deck.gamma_url} target="_blank" rel="noreferrer" className="block">
              <div className="bg-gold/10 border border-gold/40 rounded-lg p-4 hover:bg-gold/15 transition-colors flex items-center gap-3">
                <Film size={18} className="text-gold-soft" />
                <div className="flex-1">
                  <div className="font-heading text-cream">Open marketing deck in Gamma</div>
                  <div className="font-mono text-[10px] text-cream/55 mt-1">{deck.gamma_url}</div>
                </div>
                <ExternalLink size={14} className="text-gold-soft" />
              </div>
            </a>
          )}

          <Section icon={<Lightbulb size={14} />} title="Concept">
            <p className="font-body text-sm text-cream/80 leading-relaxed">{deck.video_concept}</p>
          </Section>

          <Section icon={<Megaphone size={14} />} title="Hook & duration">
            <p className="font-mono text-xs text-cream/65">
              Hook: first <span className="text-gold-soft">{deck.hook_seconds}s</span> · total <span className="text-gold-soft">{deck.duration_seconds}s</span>
            </p>
          </Section>

          <Section icon={<Sparkles size={14} />} title="Script">
            <pre className="font-mono text-xs text-cream/85 whitespace-pre-wrap leading-relaxed bg-navy/40 border border-bronze/15 rounded p-4">
              {deck.script}
            </pre>
          </Section>

          {deck.shot_list?.length > 0 && (
            <Section icon={<Camera size={14} />} title={`Shot list · ${deck.shot_list.length} setups`}>
              <div className="space-y-2">
                {deck.shot_list.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 bg-navy/30 border border-cream/8 rounded-md p-3 text-xs">
                    <div className="col-span-4">
                      <div className="eyebrow !text-bronze mb-0.5">Shot {i + 1}</div>
                      <div className="font-body text-cream">{s.shot}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="eyebrow mb-0.5">Framing</div>
                      <div className="font-mono text-[10px] text-cream/70">{s.framing}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="eyebrow mb-0.5">Talent</div>
                      <div className="font-mono text-[10px] text-cream/70">{s.talent}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="eyebrow mb-0.5">Location</div>
                      <div className="font-mono text-[10px] text-cream/70">{s.location}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="eyebrow mb-0.5">Equipment</div>
                      <div className="font-mono text-[10px] text-cream/70">{s.equipment}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DeckMeta icon={<Music size={14} />} title="Music direction" body={deck.music_direction} />
            <DeckMeta icon={<Palette size={14} />} title="Color grade" body={deck.color_grade} />
            <DeckMeta icon={<Users size={14} />} title="Talent needs" list={deck.talent_needs} />
            <DeckMeta icon={<MapPin size={14} />} title="Locations" list={deck.location_needs} />
            <DeckMeta icon={<Wrench size={14} />} title="Equipment list" list={deck.equipment_list} />
            <DeckMeta icon={<Megaphone size={14} />} title="Call to action" body={deck.cta} />
          </div>

          <Section icon={<Film size={14} />} title="RRM production breakdown">
            <p className="font-body text-sm text-cream/80 leading-relaxed whitespace-pre-wrap">{deck.rrm_production_breakdown}</p>
          </Section>

          <VaultLinkBar app={app} kind="marketing_deck" />
        </div>
      ) : (
        <div className="text-center py-12 border border-dashed border-bronze/30 rounded-lg">
          <Film size={32} className="text-bronze/60 mx-auto mb-3" />
          <p className="font-body text-cream/65 text-sm max-w-md mx-auto">
            Generate a video script, shot list, and full RRM production breakdown for this app.
            Gamma will compile it as a shareable marketing deck.
          </p>
        </div>
      )}
    </Card>
  )
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-bronze">
        {icon}
        <span className="eyebrow !text-bronze">{title}</span>
      </div>
      {children}
    </div>
  )
}

function DeckMeta({ icon, title, body, list }: { icon: React.ReactNode; title: string; body?: string; list?: string[] }) {
  return (
    <div className="bg-navy/30 border border-cream/8 rounded-md p-3">
      <div className="flex items-center gap-2 text-bronze mb-2">
        {icon}
        <span className="eyebrow !text-bronze">{title}</span>
      </div>
      {body && <p className="font-body text-xs text-cream/75 leading-relaxed">{body}</p>}
      {list && (
        <ul className="space-y-0.5">
          {list.map((x, i) => <li key={i} className="font-mono text-[10px] text-cream/70">· {x}</li>)}
        </ul>
      )}
    </div>
  )
}
